<template>
  <v-app>
    <user-setup
      :dialog="userDialog"
      :data="data"
      @update:dialog="userDialog = $event"
    />

    <v-dialog v-model="showLetterPreview" max-width="900" scrollable>
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span>{{ selectedLetterName }} Preview</span>

          <v-icon @click="showLetterPreview = false" class="cursor-pointer"
            >mdi-close</v-icon
          >
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="pa-0">
          <div class="print-container" id="letterPrintArea">
            <div v-if="invoiceHeader === true" class="watermark">
              <img
                v-if="getCurrentCompany?.logo_thumb"
                :src="`${getFileServerUrl}${getCurrentCompany.logo_thumb}`"
                alt="Company Logo"
                class="company-logo"
              />
            </div>

            <div v-if="invoiceHeader === true" class="header">
              <img
                v-if="getCurrentCompany?.logo_thumb"
                :src="`${getFileServerUrl}${getCurrentCompany.logo_thumb}`"
                alt="Company Logo"
                class="company-logo"
              />

              <div v-else class="logo-placeholder">
                <v-icon
                  icon="mdi-domain"
                  size="60"
                  color="grey-darken-2"
                ></v-icon>
              </div>
              
              <div class="company-info">
                <strong>{{ getCurrentCompany?.name || "Company Name" }}</strong
                ><br />
                {{ getCurrentCompany?.address || "Company Address" }}<br />
                Email: {{ getCurrentCompany?.email || "info@company.com" }} |
                Phone: {{ getCurrentCompany?.mobile || "+880123456789" }}
              </div>
            </div>

            <p class="letter-date">
              <strong>Date:</strong> {{ getCurrentDate }}
            </p>

            <div class="letter-body" v-html="processedLetterContent.to"></div>

            <div class="letter-subject" v-if="processedLetterContent.subject">
              <strong>Subject: {{ processedLetterContent.subject }}</strong>
            </div>

            <div class="letter-body" v-html="processedLetterContent.body"></div>

            <div v-if="processedLetterContent.terms" class="letter-terms">
              <strong>Terms & Conditions:</strong>
              <div v-html="processedLetterContent.terms"></div>
            </div>

            <div v-if="processedLetterContent.regards" class="letter-regards">
              <div v-html="processedLetterContent.regards"></div>
            </div>

            <!-- Signature Section -->
            <div class="signature-area">
              <div class="signature-box">Prepared By</div>
              <div class="signature-box">Checked By</div>
              <div class="signature-box">Approved By</div>
            </div>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="outlined" @click="printLetterPage(1)">
            <v-icon left>mdi-printer</v-icon> Print With Pad
          </v-btn>

          <v-btn color="primary" variant="outlined" @click="printLetterPage(0)">
            <v-icon left>mdi-printer</v-icon> Print Without Pad
          </v-btn>
          <v-btn color="red" variant="outlined" @click="downloadLetterPDF">
            <v-icon left>mdi-file-pdf-box</v-icon> Download PDF
          </v-btn>
          <v-btn
            variant="outlined"
            background-color="primary"
            @click="closePrintModal"
            >Close</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title
                color="blue-grey-lighten-4"
                class="panel-header"
                >Employee List</v-expansion-panel-title
              >
              <v-expansion-panel-text>
                <v-row class="py-2">
                  <v-col md="4" cols="12">
                    <v-text-field
                      v-model="search"
                      append-inner-icon="mdi-magnify"
                      label="Search"
                      single-line
                      hide-details
                      density="compact"
                      variant="outlined"
                      bg-color="grey-lighten-4"
                      class="pl-2"
                      clearable
                    ></v-text-field>
                  </v-col>

                  <v-col md="5" cols="12">
                    <div class="d-flex align-center justify-center">
                      <v-btn-group
                        variant="outlined"
                        density="compact"
                        size="x-small"
                        divided
                      >
                        <v-btn
                          prepend-icon="mdi mdi-download"
                          variant="flat"
                          color="success"
                          @click="exportToCSV"
                          >CSV</v-btn
                        >
                        <v-btn
                          prepend-icon="mdi mdi-download"
                          variant="flat"
                          color="primary"
                          @click="exportToExcel"
                          >EXCEL</v-btn
                        >
                        <v-btn
                          prepend-icon="mdi mdi-download"
                          variant="flat"
                          color="red"
                          @click="exportToPDF"
                          >PDF</v-btn
                        >
                      </v-btn-group>
                    </div>
                  </v-col>

                  <v-col md="3" cols="12">
                    <div class="text-right pr-2">
                      <v-btn
                        v-if="$insertAccess($route.fullPath)"
                        @click="goTo('/hrm/add-employee')"
                        color="primary"
                        variant="tonal"
                        prepend-icon="mdi mdi-plus"
                        rounded="xl"
                      >
                        Add Employee
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>
                <v-card class="overflow-x-auto">
                  <v-data-table
                    :headers="headers"
                    :items="getSchema"
                    :loading="loading"
                    v-model:items-per-page="itemsPerPage"
                    :search="search"
                    :hover="true"
                    density="compact"
                    class="min-w-[1000px] overflow-x-auto"
                  >
                    <template v-slot:item.index="{ index }">
                      {{ ++index }}
                    </template>

                    <template v-slot:item.user="{ item }">
                      <v-chip
                        v-if="item.user"
                        color="dark-lighten-1"
                        size="small"
                        >{{ item.user?.username }} -
                        {{ item.user?.code }}</v-chip
                      >
                    </template>

                    <template v-slot:item.status="{ item }">
                      <v-chip
                        v-if="item.status == 1"
                        color="success"
                        size="small"
                        class="text-capitalize"
                        >Active</v-chip
                      >
                      <v-chip
                        v-else
                        color="red"
                        size="small"
                        class="text-capitalize"
                        >Inactive
                      </v-chip>
                    </template>

                    <template v-slot:item.actions="{ item }">
                      <div
                        class="d-flex gap-1 justify-end align-left flex-nowrap"
                      >
                        <!-- Edit Button -->
                        <v-btn
                          icon="mdi-pencil"
                          size="small"
                          variant="text"
                          color="orange"
                          @click="editItem(item)"
                          v-if="$updateAccess($route.fullPath)"
                        />

                        <!-- Add User Button -->
                        <v-tooltip text="Add User" location="top">
                          <template
                            v-if="!item.user"
                            v-slot:activator="{ props }"
                          >
                            <v-btn
                              v-bind="props"
                              icon
                              size="small"
                              color="info"
                              variant="text"
                              @click="setPermission(item.id)"
                              v-if="
                                $updateAccess($route.fullPath) &&
                                item.tag != 'superAdmin'
                              "
                            >
                              <v-icon>mdi-account-key-outline</v-icon>
                            </v-btn>
                          </template>
                        </v-tooltip>

                        <!-- View Details Button -->
                        <v-tooltip text="View Details" location="top">
                          <template v-slot:activator="{ props }">
                            <v-btn
                              v-bind="props"
                              icon
                              size="small"
                              color="info"
                              variant="text"
                              @click="viewDetails(item)"
                            >
                              <v-icon>mdi-account-details</v-icon>
                            </v-btn>
                          </template>
                        </v-tooltip>

                        <!-- Letter Print Button with Menu -->
                        <!-- <v-tooltip text="Letter Print" location="top">
                          <template v-slot:activator="{ props }">
                            <v-btn
                              v-bind="props"
                              icon
                              size="small"
                              color="info"
                              variant="text"
                            >
                              <v-icon>mdi-file-document-multiple</v-icon>

                              <v-menu
                                :model-value="letterMenuOpen[item.id] || false"
                                @update:model-value="letterMenuOpen[item.id] = $event"
                                activator="parent"
                                location="bottom end"
                                offset="4"
                              >
                                <v-list
                                  bg-color="surface-light"
                                  class="d-flex flex-column ga-1 pa-1"
                                  density="compact"
                                  rounded="lg"
                                  variant="text"
                                  slim
                                >
                                  <div
                                    v-if="
                                      !getHrmLetterInfo ||
                                      getHrmLetterInfo.length === 0
                                    "
                                    class="pa-3"
                                  >
                                    <p class="text-caption">
                                      No letter templates available
                                    </p>
                                  </div>

                                  <v-list-item
                                    v-for="(letterItem, idx) in getHrmLetterInfo"
                                    :key="idx"
                                    prepend-icon="mdi-file-document-outline"
                                    rounded="lg"
                                    link
                                    @click="printLetter(item, letterItem)"
                                    class="cursor-pointer"
                                  >
                                    <template v-slot:title>
                                      {{ letterItem.name }}
                                    </template>
                                  </v-list-item>
                                </v-list>
                              </v-menu>
                            </v-btn>
                          </template>
                        </v-tooltip> -->

                        <!-- formatted letter print  -->

                        <v-tooltip text="Formatted Letter Print" location="top">
                          <template v-slot:activator="{ props }">
                            <v-btn
                              v-bind="props"
                              icon
                              size="small"
                              color="info"
                              variant="text"
                            >
                              <v-icon>mdi-file-multiple</v-icon>

                              <v-menu
                                :model-value="formattedLetterMenuOpen[item.id] || false"
                                @update:model-value="formattedLetterMenuOpen[item.id] = $event"
                                activator="parent"
                                location="bottom end"
                                offset="4"
                              >
                                <v-list
                                  bg-color="surface-light"
                                  class="d-flex flex-column ga-1 pa-1"
                                  density="compact"
                                  rounded="lg"
                                  variant="text"
                                  slim
                                >
                                  <v-list-item
                                    v-for="(letterItem, idx) in getHrmFormattedLetter"
                                    :key="idx"
                                    prepend-icon="mdi-file-document-outline"
                                    rounded="lg"
                                    link
                                    class="cursor-pointer"
                                    @click="viewFormattedEmployeeLetter(item, letterItem)"
                                  >
                                    <template v-slot:title>
                                      {{ letterItem.title }}
                                    </template>
                                  </v-list-item>
                                </v-list>
                                
                              </v-menu>
                            </v-btn>
                          </template>
                        </v-tooltip>

                      </div>
                    </template>
                  </v-data-table>
                </v-card>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script>
import userSetup from "@/components/module/administration/userManagement/userSetup.vue";
import departmentSetup from "@/components/module/hrm/settingsManagement/hrmDepartmentSetup.vue";
import html2pdf from "html2pdf.js";

export default {
  components: {
    userSetup,
    departmentSetup,
  },
  data: () => ({
    itemsPerPage: 10,
    panel: [0],
    loading: false,
    search: "",
    dialog: false,
    letterMenuOpen: {},
    formattedLetterMenuOpen: {},
    userDialog: false,
    data: {},
    invoiceHeader: true,
    showLetterPreview: false,
    selectedEmployee: null,
    selectedLetterName: "",
    selectedLetterTemplate: null,
    processedLetterContent: {
      to: "",
      subject: "",
      body: "",
      terms: "",
      regards: "",
    },
    headers: [
      { title: "#SL", align: "center", key: "index", sortable: true, },
      { title: "Branch", key: "branch.name", align: "left", sortable: true },
      { title: "Code", key: "code", align: "left", sortable: true },
      { title: "Name", key: "name", align: "left", sortable: true },
      { title: "Father Name", key: "father_name", align: "left", sortable: true, },
      { title: "Mobile", key: "mobile", align: "left", sortable: true },
      { title: "Email", key: "email", align: "left", sortable: true },
      { title: "Department", key: "department.name", align: "left", sortable: true, },
      { title: "Designation", key: "designation.name", align: "left", sortable: true, },
      { title: "User Name", key: "user", align: "left", sortable: true, },
      { title: "Status", align: "center", key: "status", sortable: true },
      { title: "Actions", align: "center", key: "actions", sortable: true },
    ],
  }),
  watch: {
    invoiceHeader(val) {
      console.log(val);
    },
  },
  created() {
    if (this.$store.getters.getHrmEmployeeList.length == 0) {
      this.loading = true;
      this.$store.dispatch("getHrmEmployeeList").then((response) => {
        this.loading = false;
      });
    }
    if (!this.$store.getters.getHrmLetterInfo?.data?.length) {
      this.$store.dispatch("getHrmLetterInfo");
    }
  },
  computed: {
    getSchema() {
      console.log(this.$store.getters.getHrmEmployeeList);
      return this.$store.getters.getHrmEmployeeList;
    },
    getHrmLetterInfo() {
      console.log(this.$store.getters.getHrmLetterInfo);
      return this.$store.getters.getHrmLetterInfo;
    },
    getHrmFormattedLetter() {
      const letters = [
        {
          title: "Appointment Letter",
          slug: "appointment-letter"
        },
        // {
        //   title: "Promotion Letter",
        //   slug: "promotion-letter"
        // },
        {
          title: "Increment Letter",
          slug: "increment-letter"
        },

      ];
      return letters; 
    },
    getCurrentCompany() {
      console.log(this.$store.getters.getCurrentCompany);
      return this.$store.getters.getCurrentCompany;
    },
    getFileServerUrl() {
      return this.$store.getters.getFileServerUrl;
    },
    getCurrentDate() {
      const date = new Date();
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },
  },
  methods: {
    slugify(text) {
      return text
        .toLowerCase()
        .trim()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, "");
    },
    
    closePrintModal() {
      this.showLetterPreview = false;
      this.invoiceHeader = true;
    },

    goTo(val) {
      this.$router.push({ path: val }).catch((err) => {});
    },

    viewFormattedEmployeeLetter(employee, letterItem) {
      console.log("employee",employee);
      console.log("letteritem",letterItem);
      this.$router.push({
        path: `/hrm/${this.slugify(employee.name)}/${letterItem.slug}`,
        query: { id: employee.id }
       })
    },

    editItem(item) {
      this.$store.commit("SET_EDITING_HRM_EMPLOYEE_LIST", item);
      this.$router.push({ path: "add-employee" });
    },

    setPermission(id) {
      const employee = this.getSchema.find((emp) => emp.id === id);
      if (!employee) {
        this.$toast && this.$toast.error("Employee not found");
        return;
      }
      this.data = {
        id: "",
        employee_id: employee.id || "",
        first_name: employee.name || "",
        last_name: employee.last_name || "",
        mobile: employee.mobile || "",
        email: employee.email || "",
        tagline: employee.tagline || "",
        logo: employee.logo || "",
        remarks: employee.remarks || "",
        status: employee.status || 1,
        branch_id: employee.branch_id || "",
        role_id: employee.role_id || "",
        branch: {
          company_id: (employee.branch && employee.branch.company_id) || "",
        },
      };
      this.userDialog = true;
    },

    viewDetails(item) {
      console.log("Navigating to employee details for:", item);
      console.log("Employee ID:", item.id);
      console.log("Navigation path:", `/hrm/employee-details/${item.id}`);

      this.$router
        .push({
          name: "employee-details",
          params: { id: item.id },
        })
        .then(() => {
          console.log("Navigation successful");
        })
        .catch((error) => {
          console.error("Navigation failed:", error);
        });
    },

    printLetter(employee, letterTemplate) {
      // Set selected employee and letter
      this.selectedEmployee = employee;
      this.selectedLetterName = letterTemplate.name;
      this.selectedLetterTemplate = letterTemplate;

      // Create replacement map with all employee data
      const replacements = {
        // Personal Information
        $EmployeeName$: employee.name || "N/A",
        $EmployeeCode$: employee.code || "N/A",
        $Email$: employee.email || "N/A",
        $Mobile$: employee.mobile || "N/A",
        $FatherName$: employee.father_name || "N/A",
        $MotherName$: employee.mother_name || "N/A",
        $DateOfBirth$: employee.date_of_birth || "N/A",
        $Gender$: employee.gender_id
          ? employee.gender_id === 1
            ? "Male"
            : "Female"
          : "N/A",

        // Contact Information
        $PhoneOffice$: employee.phone_office || "N/A",
        $PhoneResidence$: employee.phone_residence || "N/A",
        $EmergencyContactPerson$: employee.emergency_contact_person || "N/A",
        $EmergencyContactNo$: employee.emergency_contact_no || "N/A",

        // Address Information
        $PermanentAddress$: employee.permanent_address || "N/A",

        // Organization Information
        $CompanyName$: employee.branch?.name || "N/A",
        $CompanyCode$: employee.branch?.code || "N/A",
        $Department$: employee.department?.name || "N/A",
        $DepartmentCode$: employee.department?.code || "N/A",
        $Designation$: employee.designation?.name || "N/A",
        $DesignationCode$: employee.designation?.code || "N/A",

        // Employment Details
        $JoiningDate$: employee.joining_date || "N/A",
        $EmployeeType$: employee.employee_type?.name || "N/A",
        $Status$: employee.status === 1 ? "Active" : "Inactive",

        // Personal Details
        $BloodGroup$: employee.blood_group?.name || "N/A",
        $Religion$: employee.religion?.name || "N/A",
        $Nationality$: employee.nationality || "N/A",
        $MaritalStatus$: employee.marital_status?.name || "N/A",

        // Document Information
        $NID$: employee.nid || "N/A",
        $PassportNo$: employee.passport_no || "N/A",

        // Physical Information
        $Height$: employee.height || "N/A",
        $Weight$: employee.weight || "N/A",

        // User Account Information
        $Username$: employee.user?.username || "N/A",
        $UserFullName$: employee.user?.full_name || "N/A",
        $UserEmail$: employee.user?.email || "N/A",
      };

      let processedTo = letterTemplate.to || "";
      let processedSubject = letterTemplate.subject || "";
      let processedBody = letterTemplate.body || "";
      let processedTerms = letterTemplate.terms || "";
      let processedRegards = letterTemplate.regards || "";

      // Replace all placeholders
      Object.keys(replacements).forEach((key) => {
        const value = replacements[key];
        const regex = new RegExp(
          key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
          "g",
        );
        processedTo = processedTo.replace(regex, value);
        processedSubject = processedSubject.replace(regex, value);
        processedBody = processedBody.replace(regex, value);
        processedTerms = processedTerms.replace(regex, value);
        processedRegards = processedRegards.replace(regex, value);
      });

      // Store processed content
      this.processedLetterContent = {
        to: processedTo,
        subject: processedSubject,
        body: processedBody,
        terms: processedTerms,
        regards: processedRegards,
      };

      // Close menu and open preview
      this.letterMenuOpen[employee.id] = false;
      this.showLetterPreview = true;

      console.log("Letter Preview Ready:", this.processedLetterContent);
    },

    printLetterPage(isPad) {
      console.log(isPad);
      if (isPad === 1) {
        this.invoiceHeader = true;
      } else if (isPad === 0) {
        this.invoiceHeader = false;
      }

      setTimeout(() => {
        const printContent =
          document.getElementById("letterPrintArea").innerHTML;
        const printWindow = window.open(
          "",
          "_blank",
          `width=${screen.width}, height=${screen.height}, left=0, top=0`,
        );

        printWindow.document.write(`
          <!DOCTYPE html>
          <html lang="en">
          <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>Letter - ${this.selectedLetterName}</title>
            <style>
              * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
              }
              
              body {
                font-family: Arial, sans-serif;
                background: #fff;
                margin: 0;
                padding: 0;
              }
              
              .print-container {
                width: 800px;
                margin: auto;
                padding: 25px;
                position: relative;
                min-height: 600px;
              }
              
              .watermark {
                position: absolute;
                top: 45%;
                left: 50%;
                transform: translate(-50%, -50%) rotate(-30deg);
                z-index: 0;
                pointer-events: none;
                opacity: 0.04;
                width: 500px;
                height: 500px;
                display: flex;
                align-items: center;
                justify-content: center;
              }
              
              .watermark img {
                width: 100%;
                height: 100%;
                object-fit: contain;
                filter: grayscale(100%) brightness(0.5);
              }
              
              .header {
                text-align: center;
                border-bottom: 2px solid #333;
                padding-bottom: 10px;
                margin-bottom: 15px;
                position: relative;
                z-index: 1;
              }
              
              .header img {
                width: 120px;
                margin-bottom: 10px;
              }
              
              .logo-placeholder {
                margin-bottom: 10px;
              }
              
              .company-info {
                font-size: 14px;
                margin-top: 5px;
                color: #333;
              }
              
              .letter-date {
                margin-top: 15px;
                margin-bottom: 10px;
                font-size: 14px;
                position: relative;
                z-index: 1;
              }
              
              .employee-info {
                margin-bottom: 15px;
                font-size: 14px;
                line-height: 22px;
                color: #555;
                position: relative;
                z-index: 1;
              }
              
              .letter-subject {
                margin-top: 20px;
                margin-bottom: 15px;
                font-size: 14px;
                position: relative;
                z-index: 1;
              }
              
              .letter-body {
                margin-bottom: 15px;
                font-size: 14px;
                line-height: 1.6;
                color: #333;
                position: relative;
                z-index: 1;
              }
              
              .letter-body p {
                margin: 10px 0;
              }
              
              .letter-terms {
                margin-top: 20px;
                margin-bottom: 15px;
                font-size: 14px;
                color: #555;
                position: relative;
                z-index: 1;
              }
              
              .letter-terms > div {
                margin-top: 8px;
              }
              
              .letter-regards {
                margin-top: 30px;
                font-size: 14px;
                color: #333;
                position: relative;
                z-index: 1;
              }
              
              .signature-area {
                margin-top: 50px;
                display: flex;
                justify-content: space-between;
                gap: 20px;
                page-break-inside: avoid;
                position: relative;
                z-index: 1;
              }
              
              .signature-box {
                width: 220px;
                text-align: center;
                border-top: 1px solid #000;
                padding-top: 5px;
                font-size: 14px;
                color: #333;
              }
              
              @media print {
                body {
                  margin: 0;
                }
                .watermark {
                  position: fixed;
                  top: 45%;
                  left: 50%;
                  transform: translate(-50%, -50%) rotate(-30deg);
                  z-index: 0;
                  pointer-events: none;
                  opacity: 0.04;
                  width: 500px;
                  height: 500px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                }
                
                .watermark img {
                  width: 100%;
                  height: 100%;
                  object-fit: contain;
                  filter: grayscale(100%) brightness(0.5);
                }
                .print-container {
                  width: 100%;
                  padding: 25px;
                  position: relative;
                }
                .print-container > * {
                  position: relative;
                  z-index: 1;
                }
                .signature-area {
                  page-break-inside: avoid;
                  page-break-after: avoid;
                  margin-top: 30px !important;
                }
                thead { display: table-header-group; }
                tfoot { display: table-footer-group; }
                tr { page-break-inside: avoid; }
              }
            </style>
          </head>
          <body>
            ${printContent}
          </body>
          </html>
        `);

        printWindow.document.close();
        printWindow.moveTo(0, 0);
        printWindow.focus();

        setTimeout(() => {
          printWindow.print();
          printWindow.onafterprint = () => {
            printWindow.close();
          };
        }, 1000);
      }, 1000);
    },

    async downloadLetterPDF() {
      const element = document.getElementById("letterPrintArea");
      const opt = {
        margin: [0.5, 0.5],
        filename: `letter-${this.selectedLetterName}-${this.selectedEmployee?.code || "employee"}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          logging: false,
        },
        jsPDF: { unit: "cm", format: "a4", orientation: "portrait" },
      };
      try {
        await html2pdf().set(opt).from(element).save();
      } catch (error) {
        console.error("PDF generation failed:", error);
        // Fallback to print if PDF generation fails
        this.printInvoice();
      }
    },

    exportToPDF() {
        let serialNumber = 1;

        const columns = [
            "#SL",
            "Branch",
            "Code",
            "Name",
            "Father Name",
            "Mobile",
            "Email",
            "Department",
            "Designation",
            "Status"
        ];

        const rows = this.getSchema.map((item) => [
            serialNumber++,
            item.branch?.name || "",
            item.code || "",
            item.name || "",
            item.father_name || "",
            item.mobile || "",
            item.email || "",
            item.department?.name || "",
            item.designation?.name || "",
            item.status == 1 ? "Active" : "Inactive"
        ]);

        this.$exportTableToPDF(
            columns,
            rows,
            "Employee List",
            "employee_list_" + this.$fileDate()
        );
    },


    exportToExcel() {
        let data = [[
            "#SL",
            "Branch",
            "Code",
            "Name",
            "Father Name",
            "Mobile",
            "Email",
            "Department",
            "Designation",
            "Status"
        ]];

        let serialNumber = 1;

        this.getSchema.forEach((item) => {
            data.push([
                serialNumber++,
                item.branch?.name || "",
                item.code || "",
                item.name || "",
                item.father_name || "",
                item.mobile || "",
                item.email || "",
                item.department?.name || "",
                item.designation?.name || "",
                item.status == 1 ? "Active" : "Inactive"
            ]);
        });

        this.$exportTableToExcel(
            data,
            "employee_list_" + this.$fileDate()
        );
    },


    exportToCSV() {
        let data = [[
            "#SL",
            "Branch",
            "Code",
            "Name",
            "Father Name",
            "Mobile",
            "Email",
            "Department",
            "Designation",
            "Status"
        ]];

        let serialNumber = 1;

        this.getSchema.forEach((item) => {
            data.push([
                serialNumber++,
                item.branch?.name || "",
                item.code || "",
                item.name || "",
                item.father_name || "",
                item.mobile || "",
                item.email || "",
                item.department?.name || "",
                item.designation?.name || "",
                item.status == 1 ? "Active" : "Inactive"
            ]);
        });

        this.$exportTableToCSV(
            data,
            "employee_list_" + this.$fileDate()
        );
    }



  },
  mounted() {},
};
</script>

<style scoped>
.print-container {
  width: 800px;
  margin: auto;
  padding: 25px;
  background: white;
  position: relative;
  min-height: 600px;
}

.watermark {
  position: absolute;
  top: 30%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(-30deg);
  z-index: 0;
  pointer-events: none;
  opacity: 0.04;
  width: 500px;
  height: 500px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.watermark img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  filter: grayscale(100%) brightness(0.5);
}

.header {
  text-align: center;
  border-bottom: 2px solid #333;
  padding-bottom: 10px;
  margin-bottom: 15px;
  position: relative;
  z-index: 1;
}

.header img {
  width: 120px;
  margin-bottom: 10px;
}
</style>
